import { useState } from 'react';

interface DNSRecord {
  id: string;
  name: string;
  type: string;
  value: string;
  ttl: number;
  priority?: number;
  weight?: number;
  port?: number;
  proxied?: boolean;
}

interface FormData {
  name: string;
  type: string;
  value: string;
  ttl: string;
  priority: string;
  weight: string;
  port: string;
}

export const useRecordEditor = (
  record: DNSRecord,
  onSave: (recordId: string, updates: Partial<DNSRecord>) => Promise<void>,
  onCancel: () => void
) => {
  const [formData, setFormData] = useState<FormData>({
    name: record.name,
    type: record.type,
    value: record.value,
    ttl: record.ttl.toString(),
    priority: record.priority?.toString() || '',
    weight: record.weight?.toString() || '',
    port: record.port?.toString() || '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.type) {
      newErrors.type = 'Type is required';
    }

    if (!formData.value.trim()) {
      newErrors.value = 'Value is required';
    }

    if (!formData.ttl || isNaN(parseInt(formData.ttl)) || parseInt(formData.ttl) < 1) {
      newErrors.ttl = 'TTL must be a positive number';
    }

    if (formData.type === 'MX' && formData.priority && isNaN(parseInt(formData.priority))) {
      newErrors.priority = 'Priority must be a number';
    }

    if (formData.type === 'SRV') {
      if (!formData.priority || isNaN(parseInt(formData.priority))) {
        newErrors.priority = 'Priority is required for SRV records';
      }
      if (!formData.weight || isNaN(parseInt(formData.weight))) {
        newErrors.weight = 'Weight is required for SRV records';
      }
      if (!formData.port || isNaN(parseInt(formData.port))) {
        newErrors.port = 'Port is required for SRV records';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const updates: Partial<DNSRecord> = {
        name: formData.name,
        type: formData.type,
        value: formData.value,
        ttl: parseInt(formData.ttl),
      };

      if (formData.type === 'MX' && formData.priority) {
        updates.priority = parseInt(formData.priority);
      }

      if (formData.type === 'SRV') {
        updates.priority = parseInt(formData.priority);
        updates.weight = parseInt(formData.weight);
        updates.port = parseInt(formData.port);
      }

      await onSave(record.id, updates);
      onCancel(); // Close editor after successful save
    } catch (error) {
      console.error('Failed to save record:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const getPlaceholder = (type: string) => {
    switch (type) {
      case 'A':
        return '192.168.1.1';
      case 'AAAA':
        return '2001:db8::1';
      case 'CNAME':
        return 'example.com';
      case 'MX':
        return 'mail.example.com';
      case 'TXT':
        return '"v=spf1 include:_spf.example.com ~all"';
      case 'NS':
        return 'ns1.example.com';
      case 'SRV':
        return 'server.example.com';
      default:
        return '';
    }
  };

  return {
    formData,
    isSubmitting,
    errors,
    handleSubmit,
    handleInputChange,
    getPlaceholder,
    onCancel,
  };
};